import { getChr } from "../toolbar/tag_dialog/mapChrom";

/**
 * Stable identity for SNV / CNV rows so labels persist across queries in the same study.
 * Prefer genomic coordinates over result-list index (gene_0, gene_1, ...).
 */
export function getStableVariantKey(variant) {
    if (!variant || typeof variant !== "object") {
        return null;
    }

    // CNV-style rows
    if (variant.SV_chrom != null && variant.SV_start != null) {
        const chrom = getChr(String(variant.SV_chrom));
        const end = variant.SV_end != null ? variant.SV_end : "";
        const type = variant.SV_type != null ? variant.SV_type : "";
        const experiment = variant.Experiment_ID != null ? variant.Experiment_ID : "";
        return `cnv_${chrom}_${variant.SV_start}_${end}_${type}_${experiment}`;
    }

    // SNV germline / somatic
    if (variant.chrom != null && variant.pos != null) {
        const chrom = getChr(String(variant.chrom));
        const ref = variant.ref != null ? String(variant.ref) : "";
        const alt = variant.alt != null ? String(variant.alt) : "";
        if (ref !== "" && alt !== "") {
            return `snv_${chrom}_${variant.pos}_${ref}_${alt}`;
        }
        const gene = variant.gene_name != null ? variant.gene_name : (variant.gene != null ? variant.gene : "");
        return `snv_${chrom}_${variant.pos}_${gene}`;
    }

    return null;
}

/**
 * Assign a stable uniqueName for table selection / labeling.
 * Falls back to gene_index only when genomic fields are missing.
 */
export function assignStableUniqueName(variant, index) {
    if (!variant || typeof variant !== "object") {
        return variant;
    }
    const stable = getStableVariantKey(variant);
    if (stable) {
        variant.uniqueName = stable;
        return variant;
    }
    const gene = variant.gene_name != null ? variant.gene_name : "variant";
    variant.uniqueName = `${gene}_${index}`;
    return variant;
}

export function giveStableUniqueNames(data) {
    if (!Array.isArray(data) || data.length === 0) {
        return [];
    }
    return data.map((variant, index) => assignStableUniqueName({ ...variant }, index));
}

function normalizeChrom(value) {
    if (value === undefined || value === null) {
        return null;
    }
    return String(getChr(String(value)));
}

function normalizePos(value) {
    if (value === undefined || value === null || value === "") {
        return null;
    }
    const n = Number(value);
    return Number.isNaN(n) ? String(value) : n;
}

/**
 * True if two variant objects refer to the same genomic event.
 * Matches by stable uniqueName, or by chrom+pos (+ref/alt when present) for legacy labels.
 */
export function variantsMatch(a, b) {
    if (!a || !b) {
        return false;
    }

    const keyA = getStableVariantKey(a) || a.uniqueName;
    const keyB = getStableVariantKey(b) || b.uniqueName;
    if (keyA && keyB && keyA === keyB) {
        return true;
    }

    if (a.uniqueName && b.uniqueName && a.uniqueName === b.uniqueName) {
        return true;
    }

    // Legacy / dual lookup: chrom + pos (+ ref/alt when both sides have them)
    const chromA = normalizeChrom(a.chrom != null ? a.chrom : a.SV_chrom);
    const chromB = normalizeChrom(b.chrom != null ? b.chrom : b.SV_chrom);
    const posA = normalizePos(a.pos != null ? a.pos : a.SV_start);
    const posB = normalizePos(b.pos != null ? b.pos : b.SV_start);

    if (chromA == null || chromB == null || posA == null || posB == null) {
        return false;
    }
    if (chromA !== chromB || posA !== posB) {
        return false;
    }

    const refA = a.ref != null ? String(a.ref) : null;
    const refB = b.ref != null ? String(b.ref) : null;
    const altA = a.alt != null ? String(a.alt) : null;
    const altB = b.alt != null ? String(b.alt) : null;

    if (refA && refB && altA && altB) {
        return refA === refB && altA === altB;
    }

    // Old labels may lack ref/alt; chrom+pos is enough for dual lookup
    return true;
}

export function findLabelForVariant(variant, labelled) {
    if (!variant || !labelled || typeof labelled !== "object") {
        return "";
    }
    let found = "";
    Object.keys(labelled).forEach((label) => {
        const list = labelled[label] || [];
        if (list.some((stored) => variantsMatch(stored, variant))) {
            found = label;
        }
    });
    return found;
}

export function normalizeVariantsForLabel(variants) {
    if (!Array.isArray(variants)) {
        return [];
    }
    return variants.map((v, index) => {
        const copy = { ...v };
        assignStableUniqueName(copy, index);
        return copy;
    });
}

export function filterOutMatchingVariants(list, variantsToRemove) {
    if (!Array.isArray(list)) {
        return [];
    }
    if (!Array.isArray(variantsToRemove) || variantsToRemove.length === 0) {
        return list;
    }
    return list.filter((stored) => !variantsToRemove.some((v) => variantsMatch(stored, v)));
}

export function listIncludesAnyVariant(list, variants) {
    if (!Array.isArray(list) || !Array.isArray(variants)) {
        return false;
    }
    return list.some((stored) => variants.some((v) => variantsMatch(stored, v)));
}
